import {
  PublicClientApplication,
  type Configuration,
  type SilentRequest,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

/**
 * Entra sign in.
 *
 * Consultants authenticate with their own SoftwareOne identity, so MFA and
 * Conditional Access are inherited rather than reimplemented, and a leaver
 * loses access when their account is disabled. There is no local account store
 * and no password for this application to hold.
 */

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const apiScope = import.meta.env.VITE_API_SCOPE ?? '';

export const authConfigured = Boolean(tenantId && clientId && apiScope);

const configuration: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: {
    // Session storage rather than local: closing the browser ends the session.
    // A tool holding every customer's security posture should not leave a
    // token on disk on a consultant's laptop.
    //
    // Note for anyone porting older MSAL guidance: v5 removed
    // navigateToLoginRequestUrl and storeAuthStateInCookie. Most examples
    // online still show the v4 shape and will not compile.
    cacheLocation: 'sessionStorage',
  },
};

export const msalInstance = new PublicClientApplication(configuration);

export const loginRequest = { scopes: apiScope ? [apiScope] : [] };

/**
 * Silent first, interactive only where the account genuinely needs to
 * re-authenticate. Prompting on every call is how people learn to click through
 * prompts without reading them.
 */
export async function acquireToken(): Promise<string | null> {
  if (!authConfigured) return null;

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;

  const request: SilentRequest = { ...loginRequest, account };
  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Redirect here too. A popup would hit the same sessionStorage isolation
      // that breaks sign in, and it would do so mid session where it is even
      // harder to diagnose.
      await msalInstance.acquireTokenRedirect(request);
      return null;
    }
    throw error;
  }
}
